'use client';

import { cn } from '@/components/lib/utils';

type ShadowTone = 'soft' | 'ink' | 'pink' | 'amber' | 'mint' | 'accent';

const SHADOW: Record<ShadowTone, string> = {
  soft: 'shadow-pop-soft',
  ink: 'shadow-pop-lg',
  pink: 'shadow-pop-pink',
  amber: 'shadow-pop-amber',
  mint: 'shadow-pop-mint',
  accent: 'shadow-pop-accent',
};

interface StickerCardProps {
  children: React.ReactNode;
  className?: string;
  /** Colour of the hard offset shadow. `soft` is the neutral default. */
  shadow?: ShadowTone;
  /** Adds the wiggle-and-lift hover, for cards that are links or buttons. */
  interactive?: boolean;
  as?: 'div' | 'li' | 'article';
}

/**
 * The "sticker" surface: white card, chunky ink border, hard offset shadow.
 *
 * Hover tilts the card a degree and lifts it, which is what makes the system
 * feel tactile rather than flat. Only applied when `interactive`, so static
 * content cards don't move under the cursor for no reason.
 */
export default function StickerCard({
  children,
  className,
  shadow = 'soft',
  interactive = false,
  as: Tag = 'div',
}: StickerCardProps) {
  return (
    <Tag
      className={cn(
        'rounded-2xl border-2 border-pop-ink bg-card',
        SHADOW[shadow],
        interactive &&
          'transition-transform duration-300 ease-bounce-out hover:-rotate-1 hover:scale-[1.02] motion-reduce:transform-none motion-reduce:transition-none',
        className
      )}
    >
      {children}
    </Tag>
  );
}
